"use client";

import { useEffect } from "react";
import { useSession } from "next-auth/react";
import { withPostHog } from "@/shared/lib/observability/sinks";

function PostHogIdentify() {
  const { data: session } = useSession();

  useEffect(() => {
    if (!session?.user) return;
    const user = session.user;
    const userId = user.id ?? user.email ?? undefined;

    // posthog-js is loaded lazily after first paint (see observability/sinks), so
    // the session usually resolves BEFORE the SDK exists. withPostHog queues this
    // and replays it on arrival — the old `posthog.__loaded` guard would simply
    // have dropped the identify call in that (very common) race.
    withPostHog((posthog) => {
      // $set properties (updated every session)
      posthog.identify(userId, {
        email: user.email,
        name: user.name,
        role: user.role ?? "USER",
        gender: user.gender ?? null,
        onboarding_complete: user.onboardingComplete ?? false,
        onboarding_step: user.onboardingStep ?? 0,
      });

      // $set_once properties (only set on first identification)
      posthog.setPersonPropertiesForFlags({
        role: user.role ?? "USER",
        gender: user.gender ?? null,
      });
      posthog.capture("$set", {
        $set_once: {
          initial_role: user.role ?? "USER",
          signup_date: new Date().toISOString().split("T")[0],
        },
      });
    });
  }, [session]);

  return null;
}

export default function PostHogProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  return (
    <>
      <PostHogIdentify />
      {children}
    </>
  );
}
